/* eslint-disable react/prop-types */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  Map as MapIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function SitesGuideDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderBottom: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
          pb: 2,
          pt: 3,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 6 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.2),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${alpha(theme.palette.info.main, 0.4)}`,
            }}
          >
            <PublicIcon sx={{ fontSize: 32, color: "info.main" }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {t("sitesGuide.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("sitesGuide.subtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.1),
            },
          }}
          aria-label={t("sitesGuide.closeAria")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Overview Section */}
        <Box
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <InfoIcon sx={{ color: "info.main", fontSize: 24 }} />
            <Typography variant="h6" fontWeight="bold" color="info.main">
              {t("sitesGuide.overviewTitle")}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t("sitesGuide.overviewText")}
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: 3 }}>
          {/* Section 1: Understanding the Page Layout */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="1"
                  size="small"
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("sitesGuide.section1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("sitesGuide.section1Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AddIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section1Point1")}
                    secondary={t("sitesGuide.section1Point1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOnIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section1Point2")}
                    secondary={t("sitesGuide.section1Point2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MapIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section1Point3")}
                    secondary={t("sitesGuide.section1Point3Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Adding Your First Site */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="2"
                  size="small"
                  sx={{
                    bgcolor: "success.main",
                    color: "success.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("sitesGuide.section2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("sitesGuide.section2Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section2Step1")}
                    secondary={t("sitesGuide.section2Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section2Step2")}
                    secondary={t("sitesGuide.section2Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section2Step3")}
                    secondary={t("sitesGuide.section2Step3Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section2Step4")}
                    secondary={t("sitesGuide.section2Step4Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("sitesGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("sitesGuide.section2ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Using the Map Interface */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="3"
                  size="small"
                  sx={{
                    bgcolor: "warning.main",
                    color: "warning.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("sitesGuide.section3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("sitesGuide.section3Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <SearchIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section3Step1")}
                    secondary={t("sitesGuide.section3Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MapIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section3Step2")}
                    secondary={t("sitesGuide.section3Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOnIcon sx={{ color: "warning.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section3Step3")}
                    secondary={t("sitesGuide.section3Step3Desc")}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Managing Sites and Subsites */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="4"
                  size="small"
                  sx={{
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("sitesGuide.section4Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("sitesGuide.section4Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <AddIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section4Step1")}
                    secondary={t("sitesGuide.section4Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <VisibilityIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section4Step2")}
                    secondary={t("sitesGuide.section4Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EditIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section4Step3")}
                    secondary={t("sitesGuide.section4Step3Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DeleteIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section4Step4")}
                    secondary={t("sitesGuide.section4Step4Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="warning.main"
                  display="block"
                  gutterBottom
                >
                  ⚠️ {t("sitesGuide.important")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("sitesGuide.section4Important")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Viewing All Sites on Map */}
          <Accordion
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.05),
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label="5"
                  size="small"
                  sx={{
                    bgcolor: "error.main",
                    color: "error.contrastText",
                    fontWeight: "bold",
                  }}
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("sitesGuide.section5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("sitesGuide.section5Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <MapIcon sx={{ color: "info.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section5Step1")}
                    secondary={t("sitesGuide.section5Step1Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOnIcon sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section5Step2")}
                    secondary={t("sitesGuide.section5Step2Desc")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("sitesGuide.section5Step3")}
                    secondary={t("sitesGuide.section5Step3Desc")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  display="block"
                  gutterBottom
                >
                  💡 {t("sitesGuide.proTip")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("sitesGuide.section5ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 3 }} />

          {/* Quick Reference */}
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary.main"
              gutterBottom
            >
              📋 {t("sitesGuide.quickRefTitle")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("sitesGuide.quickRef1Title")}:</strong>{" "}
                      {t("sitesGuide.quickRef1Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("sitesGuide.quickRef2Title")}:</strong>{" "}
                      {t("sitesGuide.quickRef2Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("sitesGuide.quickRef3Title")}:</strong>{" "}
                      {t("sitesGuide.quickRef3Text")}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{t("sitesGuide.quickRef4Title")}:</strong>{" "}
                      {t("sitesGuide.quickRef4Text")}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("sitesGuide.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SitesGuideDialog;
